import { BufferCalculator } from 'business/BufferCalculator';
import React, { useMemo } from 'react';
import {
  useIngredientBufferDataPoints,
  useIngredientInfo,
  useIngredientStocks,
  useSelectedStockRows,
} from 'redux/hooks/IngredientDetail';
import { IngredientChart } from './IngredientChart';
import { getGridStepAt, getIngredient, getLablesFromChartData } from './utils/utils';

export const MixingCurve = () => {
  const stocks = useIngredientStocks();
  const selectedStockIndices = useSelectedStockRows();
  const ingInfo = useIngredientInfo();
  const bufferData = useIngredientBufferDataPoints();
  const selectedStocks = selectedStockIndices.map((i) => stocks[i]).filter(Boolean);

  const { chartData, error } = useMemo(() => {
    const chartData: { x: number; y: number }[] = [];
    let error = 'Select two buffer stocks of same concentration to display mixing curve';
    if (selectedStocks.length === 2) {
      if (
        parseFloat(selectedStocks[0].stockConcentration + '') !==
        parseFloat(selectedStocks[1].stockConcentration + '')
      ) {
        error = 'Concentration of both stocks is not same';
      } else if (
        selectedStocks[0].concentrationUnits !== selectedStocks[1].concentrationUnits
      ) {
        error = 'Concentration units of both stocks are not same';
      } else if (!selectedStocks[0].isBuffer) {
        error = 'Stock selected first is not a buffer';
      } else if (!selectedStocks[1].isBuffer) {
        error = 'Stock selected later is not a buffer';
      } else if (!selectedStocks[0].stockPh || !selectedStocks[1].stockPh) {
        error = "One of stock's pH is null or invalid";
      } else if (selectedStocks[0].stockPh === selectedStocks[1].stockPh) {
        error = 'Both stocks are same and need not to mix';
      } else {
        error = '';
        let highPhStock, lowPHStock;
        if (selectedStocks[0].stockPh > selectedStocks[1].stockPh) {
          highPhStock = selectedStocks[0];
          lowPHStock = selectedStocks[1];
        } else {
          lowPHStock = selectedStocks[0];
          highPhStock = selectedStocks[1];
        }
        const ing = getIngredient(ingInfo, bufferData);
        const bc = new BufferCalculator(ing, lowPHStock, highPhStock);
        const stop = bc.maxGraphX;
        const start = bc.minGraphX;
        const gridSteps = 101;

        for (let step = 0; step < gridSteps; step++) {
          const value = parseFloat(getGridStepAt(start, stop, step, gridSteps));
          chartData.push({ x: value, y: bc.LowFraction(value) });
        }
      }
    }
    return { chartData, error };
  }, [selectedStockIndices, stocks]);

  const axisLabels = useMemo(() => getLablesFromChartData(chartData), [chartData]);

  return (
    <div
      id="mixingcurve-main-container"
      className="dashed-border basis-full max-h-full flex flex-col align-center justify-center"
    >
      <div id="mixingcurve-title" className="p-2 font-bold basis-1/6 self-start">
        Mixing Curve
      </div>
      <div id="mixingcurve-charts" className="basis-5/6 h-5/6">
        {error ? (
          <div id="mixingcurve-chart-error">{error}</div>
        ) : (
          <IngredientChart
            xLabels={axisLabels.x}
            yLabels={axisLabels.y}
            chartData={chartData}
            xTitle="pH"
            yTitle="Low Fraction"
          />
        )}
      </div>
    </div>
  );
};
